import { useState, useEffect, useMemo } from 'react'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import { Trash2 } from 'lucide-react'
import { Api } from '../lib/api.js'
import Button from '../components/ui/Button.jsx'
import { useAuth } from '../contexts/AuthContext.jsx'
import { env } from '../config/env.js'
import { visibleFields } from '../lib/visibleFields.js'
import ProfileBar from '../components/review/ProfileBar.jsx'
import SectionBlock from '../components/review/SectionBlock.jsx'
import EditableField from '../components/review/EditableField.jsx'
import { FactPill, AddFactPill } from '../components/review/FactPill.jsx'
import ExportDropdown from '../components/review/ExportDropdown.jsx'
import ArchivesSection from '../components/review/ArchivesSection.jsx'
import { renderExportPdf } from '../exports/pdf.js'

const SECTION_TITLES = {
  bilan_personnel: 'Bilan personnel et familial',
  bilan_patrimonial: 'Bilan patrimonial',
  situation_fiscale: 'Situation fiscale',
  objectifs: 'Objectifs et projets de vie',
  strategie_investissement: "Stratégie d'investissement",
  strategie_immobiliere: 'Stratégie immobilière',
  prevoyance_protection: 'Prévoyance et protection',
  retraite: 'Préparation de la retraite',
  transmission_succession: 'Transmission et succession',
  recommandations_plan_action: 'Recommandations',
}


const KIND_LABELS = {
  decouverte: 'Découverte',
  bilan_annuel: 'Bilan annuel',
  suivi: 'Suivi',
  recommandation: 'Recommandation',
}

function formatSessionDate(iso) {
  if (!iso) return ''
  try {
    const d = new Date(iso)
    if (isNaN(d.getTime())) return ''
    return new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }).format(d)
  } catch { return '' }
}

const FIELD_LABELS = {
  situation_civile: 'Situation civile',
  profession: 'Profession',
  regime_matrimonial: 'Régime matrimonial',
  profession_conjoint: 'Profession du conjoint',
  clause_beneficiaire_conjoint: 'Clause bénéficiaire conjoint',
  pacs_concubinage: 'PACS / concubinage',
  enfants: 'Enfants',
  etudes_enfants: 'Études des enfants',
  personnes_a_charge: 'Personnes à charge',
  capacite_epargne_mensuelle: "Capacité d'épargne mensuelle",
  charges_independant: 'Charges indépendant',
  parts_sociales: 'Parts sociales',
  pension_mensuelle: 'Pension nette mensuelle',
  residence_principale_valeur: 'Résidence principale (valeur)',
  loyer_mensuel: 'Loyer mensuel',
  tmi: 'TMI',
  horizon: "Horizon d'investissement",
  profil_risque: 'Profil de risque',
}

export default function ReviewSessionPage() {
  const { id } = useParams()
  const loc = useLocation()
  const nav = useNavigate()
  const { token, onUnauthorized } = useAuth()

  const initial = loc.state?.extractedFacts || null
  const [facts, setFacts] = useState(initial)
  const [client, setClient] = useState(null)
  const [sessionMeta, setSessionMeta] = useState(null)
  const [profile, setProfile] = useState({ civilStatus: 'marie', hasChildren: true, employment: 'salarie', ownership: 'proprietaire' })
  const [profileFields, setProfileFields] = useState({})
  const [archives, setArchives] = useState([])

  // Seed selectors + atomic field values from the extractor's profile pass
  // when extractedFacts arrives (whether via nav state on first arrival or
  // via the API fetch on cold reload). Client-bar choices override the
  // extractor where the client row has explicit values.
  const applyProfileFields = (pf) => {
    if (!pf) return
    setProfile((prev) => ({
      civilStatus: pf.civil_status || prev.civilStatus,
      hasChildren: pf.has_children ?? prev.hasChildren,
      employment:  pf.employment   || prev.employment,
      ownership:   pf.ownership    || prev.ownership,
    }))
    if (pf.fields && typeof pf.fields === 'object') {
      setProfileFields((prev) => ({ ...prev, ...pf.fields }))
    }
  }

  useEffect(() => { applyProfileFields(initial?.profile_fields) }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const api = new Api({ baseURL: env.API_BASE_URL || '', getToken: () => token, onUnauthorized })
    api.get(`/api/clarity/sessions/${id}`).then(async (sess) => {
      setSessionMeta(sess)
      if (!facts) {
        const f = sess.facts || {}
        setFacts(f)
        applyProfileFields(f.profile_fields)
      }
      const cid = sess.client_id || sess.ClientID
      if (cid) {
        const c = await api.getClient(cid).catch(() => null)
        if (c) {
          setClient(c)
          // Client-row values take precedence over the extractor's guess
          // (the human-edited record is more authoritative than the LLM).
          setProfile((prev) => ({
            civilStatus: c.CivilStatus || c.civil_status || prev.civilStatus,
            hasChildren: c.HasChildren ?? c.has_children ?? prev.hasChildren,
            employment:  c.Employment  || c.employment  || prev.employment,
            ownership:   c.Ownership   || c.ownership   || prev.ownership,
          }))
        }
      }
      const list = await api.listExports(id).catch(() => [])
      setArchives(list || [])
    }).catch(() => { if (!facts) setFacts({}) })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, token, onUnauthorized])

  const visibleKeys = useMemo(() => Array.from(visibleFields(profile)), [profile])

  const refreshArchives = async () => {
    const api = new Api({ baseURL: env.API_BASE_URL || '', getToken: () => token, onUnauthorized })
    const list = await api.listExports(id).catch(() => null)
    if (list) setArchives(list)
  }

  const [saveState, setSaveState] = useState('idle') // idle | saving | saved | error

  // Snapshot of {facts, profile, profileFields} at the moment the page first
  // sees a populated state — taken either from nav-state or the API fetch.
  // Compared against the live state to drive the "is dirty" check that gates
  // the Save button. Captured once (the !baseline guard) and updated only on
  // successful save, so re-renders don't churn it.
  const [baseline, setBaseline] = useState(null)
  useEffect(() => {
    if (!baseline && facts) {
      setBaseline({
        facts: JSON.stringify(facts),
        profile: JSON.stringify(profile),
        profileFields: JSON.stringify(profileFields),
      })
    }
  }, [facts, profile, profileFields, baseline])

  const isDirty = !!(baseline && (
    baseline.facts !== JSON.stringify(facts) ||
    baseline.profile !== JSON.stringify(profile) ||
    baseline.profileFields !== JSON.stringify(profileFields)
  ))

  const buildMerged = () => ({
    ...facts,
    profile_fields: {
      civil_status: profile.civilStatus,
      has_children: profile.hasChildren,
      employment:  profile.employment,
      ownership:   profile.ownership,
      fields: profileFields,
    },
  })

  const onSave = async () => {
    if (!facts || !isDirty) return
    setSaveState('saving')
    const api = new Api({ baseURL: env.API_BASE_URL || '', getToken: () => token, onUnauthorized })
    const merged = buildMerged()
    try {
      await api.patchSessionFacts(id, merged)
      // Reset the baseline to what we just shipped so isDirty flips back to
      // false until the next edit lands.
      setBaseline({
        facts: JSON.stringify(facts),
        profile: JSON.stringify(profile),
        profileFields: JSON.stringify(profileFields),
      })
      setSaveState('saved')
      setTimeout(() => setSaveState((s) => (s === 'saved' ? 'idle' : s)), 2000)
    } catch (e) {
      console.error('save facts:', e)
      setSaveState('error')
    }
  }

  const onDelete = async () => {
    if (!window.confirm('Supprimer cette séance ? Cette action est définitive.')) return
    const api = new Api({ baseURL: env.API_BASE_URL || '', getToken: () => token, onUnauthorized })
    try {
      await api.deleteSession(id)
      nav('/', { replace: true })
    } catch (e) {
      console.error('delete session:', e)
      alert('La suppression a échoué — réessayez ou consultez les logs.')
    }
  }

  const onExport = (kind) => {
    renderExportPdf(kind, {
      sessionId: id, profile, profileFields, facts,
      // Pass session + client metadata so the PDF titles can be
      // descriptive (client name, kind, date) instead of generic.
      clientName: client?.full_name || client?.FullName || client?.Name || client?.name,
      kind: sessionMeta?.kind || sessionMeta?.Kind,
      startedAt: sessionMeta?.started_at || sessionMeta?.StartedAt,
      token, onArchived: refreshArchives,
    })
  }

  const apiClient = useMemo(() => new Api({ baseURL: env.API_BASE_URL || '', getToken: () => token }), [token])
  const downloadURLFor = (eid) => apiClient.exportDownloadURL(id, eid)

  if (!facts) return <div className="p-12 text-center text-muted">Chargement…</div>

  // Title precedence: client name > kind label + date > generic fallback.
  // Découverte sessions have no client linked, so for them we always land on
  // the kind+date branch.
  const clientName = client?.full_name || client?.FullName
  const kindLabel = sessionMeta ? KIND_LABELS[sessionMeta.kind || sessionMeta.Kind] : null
  const sessionDate = sessionMeta ? formatSessionDate(sessionMeta.started_at || sessionMeta.StartedAt) : ''
  const title = clientName
    ? clientName
    : (kindLabel || sessionDate)
        ? [kindLabel, sessionDate].filter(Boolean).join(' · ')
        : 'Relire et corriger'
  const subtitle = clientName && (kindLabel || sessionDate)
    ? [kindLabel, sessionDate].filter(Boolean).join(' · ')
    : null

  return (
    <div className="px-7 py-6 md:px-12 lg:px-20">
      <div className="mb-1 truncate text-[12px] text-muted">Séances › {clientName || kindLabel || 'Séance'}</div>
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-[20px] font-semibold tracking-[-0.02em]">{title}</h1>
          {subtitle && <div className="mt-0.5 text-[12px] text-muted">{subtitle}</div>}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={saveState === 'saved' ? 'ghost' : 'primary'}
            size="sm"
            // Disabled until something changes since the last loaded/saved
            // snapshot. Avoids round-tripping the same blob on stray clicks
            // and gives the button a clear "nothing to save" affordance.
            disabled={!facts || saveState === 'saving' || (!isDirty && saveState !== 'error')}
            onClick={onSave}
          >
            {saveState === 'saving' ? 'Enregistrement…' :
              saveState === 'saved'  ? 'Enregistré ✓' :
              saveState === 'error'  ? 'Échec — Réessayer' :
              'Enregistrer'}
          </Button>
          <ExportDropdown onExport={onExport} disabled={!facts} />
        </div>
      </div>
      <div className="mt-4">
        <ProfileBar profile={profile} onChange={setProfile} />
        {client && (
          <button
            onClick={async () => {
              const api = new Api({ baseURL: env.API_BASE_URL || '', getToken: () => token, onUnauthorized })
              try {
                await api.patchClient(client.ID || client.id, {
                  civil_status: profile.civilStatus,
                  has_children: profile.hasChildren,
                  employment: profile.employment,
                  ownership: profile.ownership,
                })
              } catch { /* swallow */ }
            }}
            className="mt-2 text-[12px] text-muted underline hover:text-ink"
          >
            ↻ Mettre à jour le profil client
          </button>
        )}
      </div>
      <SectionBlock title="Fiche profil" count={visibleKeys.length}>
        {visibleKeys.map((k) => (
          <EditableField
            key={k}
            label={FIELD_LABELS[k] || k}
            value={profileFields[k] || ''}
            onCommit={(v) => setProfileFields((p) => ({ ...p, [k]: v }))}
          />
        ))}
      </SectionBlock>
      {Object.entries(SECTION_TITLES).map(([key, title]) => {
        const items = facts[key] || []
        const updateItem = (i, patch) => {
          setFacts((prev) => ({ ...prev, [key]: (prev[key] || []).map((x, j) => j === i ? { ...x, ...patch } : x) }))
        }
        const removeItem = (i) => {
          setFacts((prev) => ({ ...prev, [key]: (prev[key] || []).filter((_, j) => j !== i) }))
        }
        const addItem = () => {
          setFacts((prev) => ({
            ...prev,
            [key]: [...(prev[key] || []), { value: '', confidence: 1, is_essentiel: false, _new: true }],
          }))
        }
        return (
          <SectionBlock key={key} title={title} count={items.length}>
            <div className="flex flex-wrap items-start gap-2">
              {items.map((f, i) => (
                <FactPill
                  key={i}
                  value={f.value}
                  lowConfidence={f.confidence != null && f.confidence < 0.7}
                  autoFocus={!!f._new}
                  onCommit={(v) => updateItem(i, { value: v, _new: false })}
                  onDelete={() => removeItem(i)}
                />
              ))}
              <AddFactPill onAdd={addItem} />
            </div>
          </SectionBlock>
        )
      })}
      <ArchivesSection archives={archives} downloadURLFor={downloadURLFor} />

      {/* Destructive zone — small and out-of-flow so it doesn't compete
          with the save/export actions at the top, but still findable. */}
      <div className="mt-12 flex justify-end border-t border-line/60 pt-4">
        <button
          onClick={onDelete}
          className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[12.5px] text-muted transition-colors hover:bg-terra-50 hover:text-terra-600"
        >
          <Trash2 size={13} strokeWidth={2} />
          Supprimer cette séance
        </button>
      </div>
    </div>
  )
}
